import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import StatusPill from '@/components/StatusPill';
import { Activity, AlertTriangle, Clock, Server, Siren, Cpu, Gauge } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend, Cell,
} from 'recharts';
import { Banner, ChartBox } from '@/components/OpsUI';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';

function KpiCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <Card
      className="p-4 bg-card border border-border shadow-[0_1px_0_rgba(255,255,255,0.04),0_10px_30px_rgba(0,0,0,0.35)] hover:border-[hsl(var(--primary)/.4)] transition-colors duration-150"
      data-testid={`kpi-card-${testid}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{label}</div>
        <Icon className={`h-4 w-4 ${accent || 'text-[hsl(var(--primary))]'}`} />
      </div>
      <div className="mt-3 text-3xl font-semibold tabular-nums font-display" data-testid={`kpi-value-${testid}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{sub}</div>}
    </Card>
  );
}

export default function OverviewPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);
  const [opsDown, setOpsDown] = useState(false);

  const load = async () => {
    try {
      const [{ data: ov }, { data: ts }] = await Promise.all([
        api.get('/dashboard/overview'),
        api.get('/dashboard/timeseries', { params: { hours: 6 } }),
      ]);
      setData(ov);
      setSeries(ts);
    } catch (e) {
      // ignore
    }
    try {
      const { data: ops } = await api.get('/ops/status');
      setOpsDown(!ops?.ok);
    } catch {
      setOpsDown(true);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, []);

  if (!data) return <div className="text-sm text-[hsl(var(--muted-foreground))]">{t('loading')}</div>;
  const k = data.kpis;

  const chartData = series.map((s) => ({
    t: new Date(s.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    latency: s.avg_latency_ms,
    up: s.up,
    warn: s.warn,
    down: s.down,
  }));

  const checksTotals = chartData.reduce(
    (acc, c) => ({ up: acc.up + c.up, warn: acc.warn + c.warn, down: acc.down + c.down }),
    { up: 0, warn: 0, down: 0 },
  );
  const checksData = [
    { name: 'UP', value: checksTotals.up, color: '#22C55E' },
    { name: 'WARN', value: checksTotals.warn, color: '#F59E0B' },
    { name: 'DOWN', value: checksTotals.down, color: '#EF4444' },
  ];

  return (
    <div className="space-y-6" data-testid="overview-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t('nav_overview')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            {i18n.language === 'en' ? 'Real-time operational status' : 'Estado operativo en tiempo real'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="overview-refresh">{t('refresh')}</Button>
      </div>

      {opsDown && (
        <div data-testid="overview-ops-down-banner">
          <Banner
            kind="error"
            title={i18n.language === 'en' ? 'Tu Iglesia App Ops API not responding' : 'La Ops API de Tu Iglesia App no responde'}
            message={i18n.language === 'en'
              ? 'Live app metrics may be unavailable right now. Open the App Monitor for details.'
              : 'Las métricas en vivo de la app pueden no estar disponibles ahora mismo. Abre el Monitor de la App para más detalle.'}
            action={(
              <Link to="/app-monitor">
                <Button size="sm" variant="outline" data-testid="overview-ops-down-link">{t('nav_app_monitor')}</Button>
              </Link>
            )}
          />
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard testid="services" icon={Server} label={t('kpi_services')} value={`${k.services_up}/${k.services_total}`}
          sub={`${k.services_down} down • ${k.services_warn} warn`} />
        <KpiCard testid="uptime" icon={Activity} label={t('kpi_uptime_24h')} value={`${k.uptime_pct_24h}%`}
          sub={`24h`} />
        <KpiCard testid="latency" icon={Gauge} label={t('kpi_latency')} value={`${Math.round(k.avg_latency_1h_ms)}ms`}
          sub={'1h avg'} />
        <KpiCard testid="alerts" icon={AlertTriangle} label={t('kpi_active_alerts')} value={k.active_alerts}
          accent="text-amber-400" />
        <KpiCard testid="incidents" icon={Siren} label={t('kpi_open_incidents')} value={k.open_incidents}
          accent="text-red-400" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2 p-5 bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">{t('services_status')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="services-grid">
            {data.services.map((s) => (
              <Link
                key={s.id} to={`/services/${s.id}`}
                className="rounded-lg border border-border bg-[hsl(var(--muted))] hover:border-[hsl(var(--primary)/.4)] p-3 transition-colors duration-150 group"
                data-testid={`service-tile-${s.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate group-hover:text-[hsl(var(--primary))]">{s.name}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 font-mono truncate">
                      {s.environment} • v{s.version}
                    </div>
                  </div>
                  <StatusPill status={s.maintenance_mode ? 'maintenance' : s.paused ? 'paused' : (s.current_status || 'unknown')} name={s.id} />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                  <div className="text-[hsl(var(--muted-foreground))]">
                    <Clock className="inline h-3 w-3 mr-1" />
                    {s.last_checked ? formatDistanceToNow(new Date(s.last_checked), { addSuffix: true }) : '—'}
                  </div>
                  <div className="text-right font-mono tabular-nums">
                    {s.last_latency_ms ? `${Math.round(s.last_latency_ms)} ms` : '—'}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Card>

        <Card className="p-5 bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">{t('recent_alerts')}</h2>
          </div>
          {data.recent_alerts.length === 0 ? (
            <div className="text-sm text-[hsl(var(--muted-foreground))] py-8 text-center">{t('no_data')}</div>
          ) : (
            <ul className="space-y-3 text-sm" data-testid="recent-alerts-list">
              {data.recent_alerts.map((a) => (
                <li key={a.id} className="flex items-start gap-3">
                  <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${a.severity === 'high' ? 'bg-red-400 animate-pulse' : 'bg-amber-400'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{a.service_name || 'BOMM'}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{a.message}</div>
                  </div>
                  <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono whitespace-nowrap">
                    {a.created_at ? formatDistanceToNow(new Date(a.created_at), { addSuffix: true }) : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5 bg-card border border-border">
          <h2 className="font-display text-lg font-semibold mb-3">{t('latency_over_time')}</h2>
          <ChartBox height={260} testid="recharts-latency">
              <AreaChart data={chartData} margin={{ left: -20, right: 12, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="lat" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00CCFF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#00CCFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="t" tick={{ fill: '#A7B6C6', fontSize: 11 }} stroke="rgba(255,255,255,0.08)" />
                <YAxis tick={{ fill: '#A7B6C6', fontSize: 11 }} stroke="rgba(255,255,255,0.08)" />
                <Tooltip contentStyle={{ background: '#0F1620', border: '1px solid rgba(0,204,255,0.25)', borderRadius: 8, color: '#EAF2F8' }} />
                <Area type="monotone" dataKey="latency" stroke="#00CCFF" strokeWidth={2} fill="url(#lat)" />
              </AreaChart>
          </ChartBox>
        </Card>
        <Card className="p-5 bg-card border border-border">
          <h2 className="font-display text-lg font-semibold mb-3">{t('checks_distribution')}</h2>
          <ChartBox height={260} testid="recharts-checks">
              <BarChart data={checksData} margin={{ left: -20, right: 12, top: 8, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" tick={{ fill: '#A7B6C6', fontSize: 11 }} stroke="rgba(255,255,255,0.08)" />
                <YAxis tick={{ fill: '#A7B6C6', fontSize: 11 }} stroke="rgba(255,255,255,0.08)" />
                <Tooltip contentStyle={{ background: '#0F1620', border: '1px solid rgba(0,204,255,0.25)', borderRadius: 8, color: '#EAF2F8' }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {checksData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
          </ChartBox>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5 bg-card border border-border">
          <h2 className="font-display text-lg font-semibold mb-3">{t('upcoming_maintenance')}</h2>
          {data.upcoming_maintenance.length === 0 ? (
            <div className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center">{t('no_data')}</div>
          ) : (
            <ul className="space-y-3 text-sm">
              {data.upcoming_maintenance.map((m) => (
                <li key={m.id} className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-sky-400" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{m.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">
                      {new Date(m.starts_at).toLocaleString()}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-5 bg-card border border-border">
          <h2 className="font-display text-lg font-semibold mb-3">{t('recent_deployments')}</h2>
          {data.recent_deployments.length === 0 ? (
            <div className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center">{t('no_data')}</div>
          ) : (
            <ul className="space-y-3 text-sm">
              {data.recent_deployments.map((d) => (
                <li key={d.id} className="flex items-start gap-3">
                  <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${d.status === 'success' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">
                      {d.service_name} <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">v{d.version}</span>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{d.notes}</div>
                  </div>
                  <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono whitespace-nowrap">
                    {d.created_at ? formatDistanceToNow(new Date(d.created_at), { addSuffix: true }) : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
